import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import {
  ActivateDelegationGQL,
  CreateDelegationGQL,
  CreateDelegationInput,
  DeactivateDelegationGQL,
  DelegationScope,
  DelegationStatusFilter,
  GetMyDelegationsGQL,
  GetOrganizationDelegationsGQL,
  PaginatedDelegationFilters,
  RevokeDelegationGQL,
  RevokeDelegationInput,
  ToggleDelegationInput,
} from '@generated/graphql';

export interface DelegationFilters {
  status?: DelegationStatusFilter;
  scope?: DelegationScope;
  delegatorIds?: string[];
  delegateIds?: string[];
  periodFrom?: string | null;
  periodTo?: string | null;
  createdAt_gte?: string | null;
  createdAt_lte?: string | null;
}

interface DelegationQueryParams extends DelegationFilters {
  take?: number;
  skip?: number;
  search?: string;
}

function buildDelegationFilters(params: DelegationQueryParams): PaginatedDelegationFilters | null {
  const filters: PaginatedDelegationFilters = {
    status: params.status,
    search: params.search || undefined,
    scope: params.scope,
    delegatorIds: params.delegatorIds?.length ? params.delegatorIds : undefined,
    delegateIds: params.delegateIds?.length ? params.delegateIds : undefined,
    periodFrom: params.periodFrom || undefined,
    periodTo: params.periodTo || undefined,
    createdAt_gte: params.createdAt_gte || undefined,
    createdAt_lte: params.createdAt_lte || undefined,
  };

  const hasFilter = Object.values(filters).some((value) => value !== undefined);
  return hasFilter ? filters : null;
}

@Injectable({
  providedIn: 'root',
})
export class DelegationsService {
  private readonly getMyDelegationsGQL = inject(GetMyDelegationsGQL);
  private readonly getOrganizationDelegationsGQL = inject(GetOrganizationDelegationsGQL);
  private readonly createDelegationGQL = inject(CreateDelegationGQL);
  private readonly revokeDelegationGQL = inject(RevokeDelegationGQL);
  private readonly activateDelegationGQL = inject(ActivateDelegationGQL);
  private readonly deactivateDelegationGQL = inject(DeactivateDelegationGQL);

  getMyDelegations(params: DelegationQueryParams = {}) {
    return this.getMyDelegationsGQL
      .watch(
        {
          take: params.take ?? 25,
          skip: params.skip ?? 0,
          filters: buildDelegationFilters(params),
        },
        { fetchPolicy: 'network-only' },
      )
      .valueChanges.pipe(
        map((result) => ({
          delegations: result.data?.myDelegations.edges.map((e) => e.node) ?? [],
          totalCount: result.data?.myDelegations.totalCount ?? 0,
          hasNextPage: result.data?.myDelegations.pageInfo.hasNextPage ?? false,
          loading: result.loading,
        })),
      );
  }

  getOrganizationDelegations(params: DelegationQueryParams = {}) {
    return this.getOrganizationDelegationsGQL
      .watch(
        {
          take: params.take ?? 25,
          skip: params.skip ?? 0,
          filters: buildDelegationFilters(params),
        },
        { fetchPolicy: 'network-only' },
      )
      .valueChanges.pipe(
        map((result) => ({
          delegations: result.data?.organizationDelegations.edges.map((e) => e.node) ?? [],
          totalCount: result.data?.organizationDelegations.totalCount ?? 0,
          hasNextPage: result.data?.organizationDelegations.pageInfo.hasNextPage ?? false,
          loading: result.loading,
        })),
      );
  }

  createDelegation(input: CreateDelegationInput) {
    return this.createDelegationGQL
      .mutate({ input }, { refetchQueries: ['GetMyDelegations', 'GetOrganizationDelegations'] })
      .pipe(map((result) => result.data?.createDelegation));
  }

  revokeDelegation(input: RevokeDelegationInput) {
    return this.revokeDelegationGQL
      .mutate({ input }, { refetchQueries: ['GetMyDelegations', 'GetOrganizationDelegations'] })
      .pipe(map((result) => result.data?.revokeDelegation));
  }

  activateDelegation(input: ToggleDelegationInput) {
    return this.activateDelegationGQL
      .mutate({ input }, { refetchQueries: ['GetMyDelegations', 'GetOrganizationDelegations'] })
      .pipe(map((result) => result.data?.activateDelegation));
  }

  deactivateDelegation(input: ToggleDelegationInput) {
    return this.deactivateDelegationGQL
      .mutate({ input }, { refetchQueries: ['GetMyDelegations', 'GetOrganizationDelegations'] })
      .pipe(map((result) => result.data?.deactivateDelegation));
  }
}
